<main class="page">
  <div class="steps">
    @for (step of steps(); track step.number) {
      <button
        class="step"
        [class.active]="step.active"
        [class.completed]="step.completed"
        type="button"
        [disabled]="!isStepClickable(step)"
        (click)="goToStep(step)"
      >
        <span class="step-number">{{ step.number }}</span>
        <span class="step-label">{{ step.label }}</span>
      </button>
    }
  </div>

  <h1 class="page-title">4. Response Page</h1>

  <section class="card border-0 shadow-sm user-card">
    <div class="card-body d-flex align-items-center gap-3 flex-wrap">
      @if (assignedEstimator().name) {
        <div class="user-block d-flex align-items-center gap-3">
          <div class="user-card__avatar">{{ assignedEstimator().initials }}</div>
          <div>
            <p class="user-card__name mb-0">{{ assignedEstimator().name }}</p>
            <p class="user-card__meta mb-0">TCO Estimator · {{ assignedEstimator().positionName }}</p>
          </div>
        </div>
      } @else {
        <div class="user-block d-flex align-items-center gap-3">
          <div class="user-card__avatar">?</div>
          <div>
            <p class="user-card__name mb-0">Estimator is not assigned</p>
            <p class="user-card__meta mb-0">This request has not been assigned to an Estimator yet.</p>
          </div>
        </div>
      }

      @if (form().secondaryEstimator.trim()) {
        <div class="user-block user-block--b2b d-flex align-items-center gap-3 user-block-selected">
          <div class="user-card__avatar">{{ getInitials(form().secondaryEstimator) }}</div>
          <div>
            <p class="user-card__name mb-0">{{ form().secondaryEstimator }}</p>
            <p class="user-card__meta mb-0">TCO Secondary Estimator · Cost Estimator</p>
          </div>
          <button
            type="button"
            class="user-block-close"
            (click)="updateField('secondaryEstimator', '')"
            aria-label="Remove secondary estimator"
            [disabled]="isReadOnly()"
          >
            ×
          </button>
        </div>
      } @else {
        <div class="user-block user-block--b2b user-input-block d-flex flex-column">
          <label class="field-label mb-1">Secondary Estimator</label>
          <input
            class="field-input field-select-input"
            [ngModel]="form().secondaryEstimator"
            (ngModelChange)="onUserFieldChange('secondaryEstimator', $event)"
            placeholder="Add secondary estimator"
            autocomplete="off"
            [matAutocomplete]="secondaryEstimatorOptionsList"
            #secondaryEstimatorTrigger="matAutocompleteTrigger"
            (focus)="openDropdown('secondaryEstimator', secondaryEstimatorTrigger)"
            (click)="openDropdown('secondaryEstimator', secondaryEstimatorTrigger)"
            (blur)="closeDropdown('secondaryEstimator'); validateUserField('secondaryEstimator')"
            [disabled]="isReadOnly()"
          />
          <mat-autocomplete #secondaryEstimatorOptionsList="matAutocomplete">
            @for (user of getDropdownOptions(userOptions, 'secondaryEstimator', form().secondaryEstimator); track user) {
              <mat-option [value]="user">{{ user }}</mat-option>
            }
          </mat-autocomplete>
        </div>
      }

      @if (form().reviewer.trim()) {
        <div class="user-block user-block--b2b d-flex align-items-center gap-3 user-block-selected">
          <div class="user-card__avatar">{{ getInitials(form().reviewer) }}</div>
          <div>
            <p class="user-card__name mb-0">{{ form().reviewer }}</p>
            <p class="user-card__meta mb-0">TCO Reviewer · Cost Estimator</p>
          </div>
          <button
            type="button"
            class="user-block-close"
            (click)="updateField('reviewer', '')"
            aria-label="Remove reviewer"
            [disabled]="isReadOnly()"
          >
            ×
          </button>
        </div>
      } @else {
        <div class="user-block user-block--b2b user-input-block d-flex flex-column">
          <label class="field-label mb-1">Reviewer</label>
          <input
            class="field-input field-select-input"
            [ngModel]="form().reviewer"
            (ngModelChange)="onUserFieldChange('reviewer', $event)"
            placeholder="Add reviewer"
            autocomplete="off"
            [matAutocomplete]="reviewerOptionsList"
            #reviewerTrigger="matAutocompleteTrigger"
            (focus)="openDropdown('reviewer', reviewerTrigger)"
            (click)="openDropdown('reviewer', reviewerTrigger)"
            (blur)="closeDropdown('reviewer'); validateUserField('reviewer')"
            [disabled]="isReadOnly()"
          />
          <mat-autocomplete #reviewerOptionsList="matAutocomplete">
            @for (user of getDropdownOptions(userOptions, 'reviewer', form().reviewer); track user) {
              <mat-option [value]="user">{{ user }}</mat-option>
            }
          </mat-autocomplete>
        </div>
      }

      <span class="badge rounded user-card__sso ms-auto">● Signed in via SSO</span>
    </div>
  </section>

  <section class="response-card">
    <div class="response-row response-row--two">
      <div class="field-group">
        <label class="field-label">CE Request Status <span class="required">*</span></label>
        <input
          class="field-input field-select-input"
          [class.is-invalid]="isFieldInvalid('status')"
          [ngModel]="form().status"
          (ngModelChange)="onDropdownChange('status', $event)"
          placeholder="— Select or type —"
          autocomplete="off"
          [matAutocomplete]="statusOptionsList"
          #statusTrigger="matAutocompleteTrigger"
          (focus)="openDropdown('status', statusTrigger)"
          (click)="openDropdown('status', statusTrigger)"
          (blur)="closeDropdown('status')"
          [disabled]="isReadOnly()"
        />
        <mat-autocomplete #statusOptionsList="matAutocomplete">
          @for (status of getDropdownOptions(statusOptions, 'status', form().status); track status) {
            <mat-option [value]="status">{{ status }}</mat-option>
          }
        </mat-autocomplete>
        @if (isFieldInvalid('status')) {
          <p class="field-error">CE Request Status is required.</p>
        }
      </div>

      <div class="field-group">
        <label class="field-label">Date Completed @if (isIssuedStatus()) { <span class="required">*</span> }</label>
        <input
          class="field-input"
          [class.is-invalid]="isDateCompletedInvalid()"
          type="date"
          [ngModel]="form().dateCompleted"
          (ngModelChange)="updateField('dateCompleted', $event)"
          [disabled]="isReadOnly() || !isIssuedStatus()"
          #dateInput
          (click)="openDatePicker($event)"
        />
        @if (isDateCompletedInvalid()) {
          <p class="field-error">Date Completed is required for Issued status.</p>
        }
      </div>
    </div>

    <div class="response-row response-row--four">
      <div class="field-group">
        <label class="field-label">Alternative Number <span class="required">*</span></label>
        <input
          class="field-input field-select-input"
          [class.is-invalid]="isFieldInvalid('alternativeNumber')"
          [ngModel]="form().alternativeNumber"
          (ngModelChange)="onDropdownChange('alternativeNumber', $event)"
          placeholder="— Select or type —"
          autocomplete="off"
          [matAutocomplete]="alternativeNumberOptionsList"
          #alternativeNumberTrigger="matAutocompleteTrigger"
          (focus)="openDropdown('alternativeNumber', alternativeNumberTrigger)"
          (click)="openDropdown('alternativeNumber', alternativeNumberTrigger)"
          (blur)="closeDropdown('alternativeNumber')"
          [disabled]="isReadOnly()"
        />
        <mat-autocomplete #alternativeNumberOptionsList="matAutocomplete">
          @for (option of getDropdownOptions(alternativeNumberOptions, 'alternativeNumber', form().alternativeNumber); track option) {
            <mat-option [value]="option">{{ option }}</mat-option>
          }
        </mat-autocomplete>
        @if (isFieldInvalid('alternativeNumber')) {
          <p class="field-error">Alternative Number is required.</p>
        }
      </div>

      <div class="field-group">
        <label class="field-label">Type <span class="required">*</span></label>
        <input
          class="field-input field-select-input"
          [class.is-invalid]="isFieldInvalid('type')"
          [ngModel]="form().type"
          (ngModelChange)="onDropdownChange('type', $event)"
          placeholder="— Select or type —"
          autocomplete="off"
          [matAutocomplete]="typeOptionsList"
          #typeTrigger="matAutocompleteTrigger"
          (focus)="openDropdown('type', typeTrigger)"
          (click)="openDropdown('type', typeTrigger)"
          (blur)="closeDropdown('type')"
          [disabled]="isReadOnly()"
        />
        <mat-autocomplete #typeOptionsList="matAutocomplete">
          @for (option of getDropdownOptions(typeOptions, 'type', form().type); track option) {
            <mat-option [value]="option">{{ option }}</mat-option>
          }
        </mat-autocomplete>
        @if (isFieldInvalid('type')) {
          <p class="field-error">Type is required.</p>
        }
      </div>

      <div class="field-group">
        <label class="field-label">Revision <span class="required">*</span></label>
        <input
          class="field-input field-select-input"
          [class.is-invalid]="isFieldInvalid('revision')"
          [ngModel]="form().revision"
          (ngModelChange)="onDropdownChange('revision', $event)"
          placeholder="— Select or type —"
          autocomplete="off"
          [matAutocomplete]="revisionOptionsList"
          #revisionTrigger="matAutocompleteTrigger"
          (focus)="openDropdown('revision', revisionTrigger)"
          (click)="openDropdown('revision', revisionTrigger)"
          (blur)="closeDropdown('revision')"
          [disabled]="isReadOnly()"
        />
        <mat-autocomplete #revisionOptionsList="matAutocomplete">
          @for (option of getDropdownOptions(revisionOptions, 'revision', form().revision); track option) {
            <mat-option [value]="option">{{ option }}</mat-option>
          }
        </mat-autocomplete>
        @if (isFieldInvalid('revision')) {
          <p class="field-error">Revision is required.</p>
        }
      </div>

      <div class="field-group">
        <label class="field-label">Contingency Calculator Method <span class="required">*</span></label>
        <input
          class="field-input field-select-input"
          [class.is-invalid]="isFieldInvalid('contingencyCalculationMethod')"
          [ngModel]="form().contingencyCalculationMethod"
          (ngModelChange)="onDropdownChange('contingencyCalculationMethod', $event)"
          placeholder="— Select or type —"
          autocomplete="off"
          [matAutocomplete]="contingencyOptionsList"
          #contingencyTrigger="matAutocompleteTrigger"
          (focus)="openDropdown('contingencyCalculationMethod', contingencyTrigger)"
          (click)="openDropdown('contingencyCalculationMethod', contingencyTrigger)"
          (blur)="closeDropdown('contingencyCalculationMethod')"
          [disabled]="isReadOnly()"
        />
        <mat-autocomplete #contingencyOptionsList="matAutocomplete">
          @for (option of getDropdownOptions(contingencyOptions, 'contingencyCalculationMethod', form().contingencyCalculationMethod); track option) {
            <mat-option [value]="option">{{ option }}</mat-option>
          }
        </mat-autocomplete>
        @if (isFieldInvalid('contingencyCalculationMethod')) {
          <p class="field-error">Contingency Calculator Method is required.</p>
        }
      </div>
    </div>

    <section class="comments-card">
      <div class="comments-head">
        <div class="comments-title-wrap">
          <h2 class="comments-title mb-0">Comments</h2>
          <span class="comments-count">{{ previousComments().length }}</span>
        </div>
        <button type="button" class="comments-sort" (click)="toggleCommentSortOrder()">
          Sort by: {{ commentSortOrder() === 'oldest' ? 'Oldest' : 'Newest' }}
        </button>
      </div>

      <div class="comments-timeline">
        @for (comment of sortedComments(); track comment.createdAt + comment.author) {
          <div class="timeline-row">
            <div class="timeline-date">{{ getCommentDate(comment) }}</div>
            <div class="timeline-dot"></div>
            <article class="timeline-card">
              <div class="timeline-card__head">
                <div>
                  <span class="timeline-author">{{ comment.author }}</span>
                  <span class="timeline-role">{{ comment.role }}</span>
                </div>
              </div>
              <p class="timeline-message mb-0">{{ comment.message }}</p>
            </article>
          </div>
        } @empty {
          <p class="comments-empty mb-0">No comments yet.</p>
        }
      </div>
    </section>

    <section class="attachments-card">
      <div class="attachments-head">
        <div class="attachments-title-wrap">
          <h2 class="attachments-title mb-0">Attachments</h2>
          <span class="attachments-count">{{ attachments().length }}</span>
        </div>
      </div>

      <div class="attachments-list">
        @if (hasAttachments()) {
          @for (attachment of attachments(); track attachment.id) {
            <article class="attachment-row">
              <div class="attachment-main">
                <div class="attachment-name">{{ attachment.fileName }}</div>
                <div class="attachment-meta">
                  <span>{{ attachment.createdBy }}</span>
                  <span>{{ attachment.createdDate }}</span>
                  <span>{{ formatAttachmentSize(attachment.fileSizeBytes) }}</span>
                </div>
              </div>
              <div class="attachment-actions">
                <button type="button" class="btn btn-attachment" (click)="downloadAttachment(attachment)">Download</button>
                @if (canDeleteAttachments()) {
                  <button type="button" class="btn btn-attachment btn-attachment--danger" (click)="deleteAttachment(attachment)">Delete</button>
                }
              </div>
            </article>
          }
        } @else {
          <p class="attachments-empty mb-0">No attachments yet.</p>
        }
      </div>
    </section>

    @if (savedStatus() !== 'Issued' && savedStatus() !== 'Canceled') {
      <div class="actions-row">
        <button type="button" class="btn btn-cancel" (click)="cancel()" [disabled]="isSubmitting()">Cancel</button>
        <button type="button" class="btn btn-submit" (click)="submit()" [disabled]="isReadOnly() || isSubmitting()">
          {{ isSubmitting() ? 'Submitting...' : 'Submit' }}
        </button>
      </div>
    }
  </section>
</main>
